import React from "react";
import { NodeProps, useReactFlow } from "reactflow";
import styled from 'styled-components';
import axios from 'axios';
import { operationType } from "../../../../../services/types";
import { updateNodeValue } from "../../../../../services/method";
import { Textarea } from "../../../../form/textarea";
import { SettingsBlock, settingsDataType } from "../../../../settingsBlock";
import { downloadBlobFile, DOC_EXT_ICONS } from '../../../../../services/fileDownload';
import { IoCheckmarkCircleOutline, IoWarningOutline } from "react-icons/io5";
import { ExpandedBlock } from "../../../expanded";
import { webURL } from '../../../../../services/API';
import Cookies from 'universal-cookie';

type DocumentIntelligenceProps = NodeProps<operationType> & {
    setChildMinimize?: (fn: (id: string) => void) => void;
    setChildSettings?: (fn: (id: string) => void) => void;
};

const DOC_TYPES = [
    { value: 'pptx', label: '🟦 PowerPoint (.pptx)' },
    { value: 'docx', label: '🟩 Word (.docx)' },
    { value: 'xlsx', label: '🟨 Excel (.xlsx)' },
    { value: 'pdf',  label: '🟥 PDF (.pdf)' },
];

const TONES = [
    { value: 'professional', label: 'Professional' },
    { value: 'casual',       label: 'Casual' },
    { value: 'technical',    label: 'Technical' },
];

const LENGTHS = [
    { value: 'short',  label: 'Short' },
    { value: 'medium', label: 'Medium' },
    { value: 'long',   label: 'Long' },
];

const EXT_ICONS = DOC_EXT_ICONS;

export function DocumentIntelligence({ id, data, setChildMinimize, setChildSettings }: DocumentIntelligenceProps) {
    const { setNodes, getEdges } = useReactFlow();
    const [settings, setSettings] = React.useState<settingsDataType>({ isToggle: false });
    const [isExpanded, setIsExpanded] = React.useState(false);
    const [mode, setMode] = React.useState<'generate' | 'template'>(data.value?.template_mode ? 'template' : 'generate');
    const [templateVariables, setTemplateVariables] = React.useState<string[]>([]);
    const [variableValues, setVariableValues] = React.useState<Record<string, string>>({});
    const [templateSourceUrl, setTemplateSourceUrl] = React.useState<string>(data.value?.template_source_url || '');
    const [templateFileName, setTemplateFileName] = React.useState<string>(data.value?.template_file_name || '');
    const [valuesSaved, setValuesSaved] = React.useState(false);
    const [isExtracting, setIsExtracting] = React.useState(false);
    const [isUploading, setIsUploading] = React.useState(false);
    const [extractError, setExtractError] = React.useState('');
    const cookies = new Cookies();
    const userToken = cookies.get('token');

    React.useEffect(() => {
        if (setChildMinimize) setChildMinimize(() => { setIsExpanded(prev => !prev); });
        if (setChildSettings) setChildSettings(() => { setSettings(prev => ({ ...prev, isToggle: !prev.isToggle })); });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [setChildMinimize, setChildSettings]);

    React.useEffect(() => {
        if (!data.value) {
            setNodes((nds) => nds.map((node) => {
                if (node.id === id) return {
                    ...node,
                    data: {
                        ...node.data,
                        value: {
                            request: 'agent_decide',
                            doc_type: 'docx',
                            topic: '',
                            instructions: '',
                            tone: 'professional',
                            length: 'medium',
                            style_reference: '',
                        }
                    }
                };
                return node;
            }));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Restore template state from saved node value (survives reopen / remount)
    React.useEffect(() => {
        const savedUrl = data.value?.template_source_url;
        if (savedUrl && !templateSourceUrl) setTemplateSourceUrl(savedUrl);
        if (data.value?.template_file_name && !templateFileName) {
            setTemplateFileName(data.value.template_file_name);
        }

        const savedVars = data.value?.template_variables;
        if (savedVars && typeof savedVars === 'string') {
            try {
                const parsed = JSON.parse(savedVars);
                setVariableValues(parsed);
                const keys = Object.keys(parsed);
                if (keys.length > 0 && templateVariables.length === 0) {
                    setTemplateVariables(keys);
                }
            } catch {}
        }

        const savedNames = data.value?.template_variable_names;
        if (Array.isArray(savedNames) && savedNames.length > 0 && templateVariables.length === 0) {
            setTemplateVariables(savedNames);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data.value?.template_source_url, data.value?.template_variables, data.value?.template_variable_names]);

    const handleSelect = (key: string) => (e: React.ChangeEvent<HTMLSelectElement>) => {
        updateNodeValue(id, setNodes, { [key]: e.target.value });
    };

    const handleText = (key: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        updateNodeValue(id, setNodes, { [key]: e.target.value });
    };

    const handleModeChange = (newMode: 'generate' | 'template') => {
        setMode(newMode);
        updateNodeValue(id, setNodes, { 
            template_mode: newMode === 'template',
            template_variables: newMode === 'template' ? data.value?.template_variables : undefined,
            template_source_url: newMode === 'template' ? (templateSourceUrl || data.value?.template_source_url) : data.value?.template_source_url,
        });
        if (newMode === 'generate') {
            setTemplateVariables([]);
            setVariableValues({});
            setValuesSaved(false);
        }
    };

    const handleTemplateUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const target = event.target as HTMLInputElement;
        if (!target.files || !target.files[0]) return;
        const file = target.files[0];

        if (!file.name.toLowerCase().endsWith('.docx')) {
            setExtractError('Only .docx files are supported');
            return;
        }

        setIsUploading(true);
        setExtractError('');
        setTemplateVariables([]);
        setValuesSaved(false);

        try {
            const formData = new FormData();
            formData.append('file', file);
            const uploadResponse = await axios.post(`${webURL}/general/uploader`, formData, {
                headers: { 'Content-Type': 'multipart/form-data', 'x-auth-token': userToken }
            });
            const fileUrl = uploadResponse.data?.url || uploadResponse.data?.file_url || '';
            if (!fileUrl) {
                setExtractError('Upload succeeded but no file URL was returned');
                return;
            }

            // Keep URL in local state so the save button stays enabled even if node data is slow to update
            setTemplateSourceUrl(fileUrl);
            setTemplateFileName(file.name);
            updateNodeValue(id, setNodes, {
                template_mode: true,
                template_source_url: fileUrl,
                template_file_name: file.name,
            });

            setIsExtracting(true);
            const extractResponse = await axios.post(
                `${webURL}/document-intelligence/extract-template-variables`,
                { file_url: fileUrl },
                { headers: { 'x-auth-token': userToken } }
            );

            if (extractResponse.data.success) {
                const vars: string[] = extractResponse.data.variables || [];
                setTemplateVariables(vars);
                const initialValues: Record<string, string> = {};
                vars.forEach((v: string) => {
                    initialValues[v] = '';
                });
                setVariableValues(initialValues);
                updateNodeValue(id, setNodes, {
                    template_mode: true,
                    template_source_url: fileUrl,
                    template_file_name: file.name,
                    template_variable_names: vars,
                });
            } else {
                setExtractError(extractResponse.data.error || 'Failed to extract variables');
            }
        } catch (err: any) {
            setExtractError(err?.response?.data?.error || err?.message || 'Failed to process template');
        } finally {
            setIsUploading(false);
            setIsExtracting(false);
            target.value = '';
        }
    };

    const handleVariableChange = (varName: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
        const newValues = { ...variableValues, [varName]: e.target.value };
        setVariableValues(newValues);
        setValuesSaved(false);
    };

    const resolvedTemplateUrl = templateSourceUrl || data.value?.template_source_url || '';

    const handleGenerateTemplate = () => {
        if (!resolvedTemplateUrl) {
            setExtractError('Please upload a template DOCX first');
            return;
        }
        updateNodeValue(id, setNodes, {
            template_mode: true,
            template_source_url: resolvedTemplateUrl,
            template_file_name: templateFileName || data.value?.template_file_name,
            template_variable_names: templateVariables,
            template_variables: JSON.stringify(variableValues),
        });
        setValuesSaved(true);
    };

    const canSaveTemplateValues = Boolean(resolvedTemplateUrl) && templateVariables.length > 0;
    const isWarning = mode === 'generate' ? !data.value?.topic : !resolvedTemplateUrl;

    let docOutput: any = null;
    if (data.output) {
        try {
            const parsed = JSON.parse(data.output);
            if (parsed?.type === 'document_generation') docOutput = parsed;
        } catch { /* not JSON */ }
    }

    return (<>
        <SettingsBlock nodeId={id} data={settings} setData={setSettings} />
        <Main>
            {docOutput?.success && docOutput.file_url && (
                <div className="doc-output-preview">
                    <div className="doc-icon">{EXT_ICONS[docOutput.file_type] || '📄'}</div>
                    <div className="doc-output-meta">
                        <span className="doc-name">{docOutput.file_name}</span>
                        {docOutput.metadata && (
                            <span className="doc-meta-detail">
                                {docOutput.metadata.slides != null && `${docOutput.metadata.slides} slides`}
                                {docOutput.metadata.pages != null && `${docOutput.metadata.pages} pages`}
                                {docOutput.metadata.sheets != null && `${docOutput.metadata.sheets} sheets`}
                            </span>
                        )}
                    </div>
                    <button
                        className="doc-download-btn"
                        onClick={() => downloadBlobFile(docOutput.file_url, docOutput.file_name)}
                    >
                        ↓ Download
                    </button>
                </div>
            )}
            {docOutput && !docOutput.success && (
                <div className="doc-error">⚠ {docOutput.error || 'Generation failed'}</div>
            )}
            {isWarning ? <>
                <div id="warning"></div>
                <span id="warningIcon"><IoWarningOutline size={21} /></span>
            </> : data.output ? <>
                <div id="ran"></div>
                <span id="ranIcon"><IoCheckmarkCircleOutline size={21} /></span>
            </> : null}

            {isExpanded ? <ExpandedBlock
                data={{ id, ...data }}
                handleClose={() => setIsExpanded(false)}
                isWarning={isWarning}
                style={{ minWidth: '440px' }}
            ><Styled>

                <div className="mode-toggle">
                    <button 
                        className={`mode-btn ${mode === 'generate' ? 'active' : ''}`}
                        onClick={() => handleModeChange('generate')}
                    >
                        📝 Generate
                    </button>
                    <button 
                        className={`mode-btn ${mode === 'template' ? 'active' : ''}`}
                        onClick={() => handleModeChange('template')}
                    >
                        📋 Template
                    </button>
                </div>

                {mode === 'generate' ? (
                    <>
                        <div className="section-divider">
                            <span className="section-title">📄 Document Generation</span>
                        </div>

                        <div className="row-2">
                            <div className="field-group">
                                <label>Document Type</label>
                                <select value={data.value?.doc_type || 'docx'} onChange={handleSelect('doc_type')}>
                                    {DOC_TYPES.map(t => (
                                        <option key={t.value} value={t.value}>{t.label}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="field-group">
                                <label>Length</label>
                                <select value={data.value?.length || 'medium'} onChange={handleSelect('length')}>
                                    {LENGTHS.map(l => (
                                        <option key={l.value} value={l.value}>{l.label}</option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        <div className="field-group">
                            <label>Topic</label>
                            <input
                                type="text"
                                value={data.value?.topic || ''}
                                placeholder="e.g. Q3 Sales Report, Marketing Strategy..."
                                onChange={handleText('topic')}
                            />
                        </div>

                        <div className="field-group">
                            <label>Instructions (optional)</label>
                            <Textarea
                                value={data.value?.instructions || ''}
                                placeholder="Any specific requirements, sections to include, data to use..."
                                handleChange={(e: any) => updateNodeValue(id, setNodes, { instructions: e.target.value })}
                            />
                        </div>

                        <div className="field-group">
                            <label>Tone</label>
                            <select value={data.value?.tone || 'professional'} onChange={handleSelect('tone')}>
                                {TONES.map(t => (
                                    <option key={t.value} value={t.value}>{t.label}</option>
                                ))}
                            </select>
                        </div>

                        <div className="field-group">
                            <label>Style Reference (optional)</label>
                            <Textarea
                                value={data.value?.style_reference || ''}
                                placeholder="Paste a style profile or describe the writing style to match (e.g. from an uploaded document's style analysis)..."
                                handleChange={(e: any) => updateNodeValue(id, setNodes, { style_reference: e.target.value })}
                            />
                        </div>

                        <div className="helper-text">AI generates structured content · Backend converts to real file</div>
                    </>
                ) : (
                    <>
                        <div className="section-divider">
                            <span className="section-title">📋 Template Mode</span>
                        </div>

                        {(() => {
                            const edges = getEdges ? getEdges() : [];
                            const hasUpstream = edges.some((e: any) => e.target === id);
                            if (hasUpstream) {
                                return (
                                    <div className="upstream-hint">
                                        <span className="upstream-hint-icon">🔗</span>
                                        <span className="upstream-hint-text">Connected to upstream node(s). Template variables may be auto-filled at runtime by a connected AI Agent.</span>
                                    </div>
                                );
                            }
                            return null;
                        })()}

                        <div className="field-group">
                            <label>Upload Template (.docx)</label>
                            <div className="upload-area">
                                <input 
                                    type="file" 
                                    accept=".docx" 
                                    onChange={handleTemplateUpload}
                                    disabled={isUploading || isExtracting}
                                    id={`template-upload-${id}`}
                                />
                                <label htmlFor={`template-upload-${id}`} className="upload-label">
                                    {isUploading ? '⏳ Uploading...' : isExtracting ? '⏳ Extracting variables...' : '📁 Choose DOCX file'}
                                </label>
                            </div>
                            {extractError && <div className="extract-error">⚠ {extractError}</div>}
                            {resolvedTemplateUrl && !extractError && (
                                <div className="template-info">
                                    ✓ Template ready{templateFileName ? `: ${templateFileName}` : ''}
                                </div>
                            )}
                        </div>

                        {templateVariables.length > 0 && (
                            <>
                                <div className="variables-section">
                                    <label>Template Variables ({templateVariables.length})</label>
                                    <div className="variables-list">
                                        {templateVariables.map(varName => (
                                            <div key={varName} className="variable-input">
                                                <label>{varName}</label>
                                                <input
                                                    type="text"
                                                    value={variableValues[varName] || ''}
                                                    onChange={handleVariableChange(varName)}
                                                    placeholder={`Enter ${varName}...`}
                                                />
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                <button 
                                    className="generate-btn"
                                    onClick={handleGenerateTemplate}
                                    disabled={!canSaveTemplateValues}
                                    type="button"
                                >
                                    {valuesSaved ? '✓ Values Saved' : '✓ Set Values & Generate'}
                                </button>
                                {!canSaveTemplateValues && (
                                    <div className="extract-error">⚠ Re-upload the template DOCX so the file URL is saved, then click again.</div>
                                )}
                                {valuesSaved && (
                                    <div className="template-info">Values saved on this node. Run the workflow to generate the filled DOCX.</div>
                                )}
                            </>
                        )}

                        <div className="helper-text">Upload a DOCX with {'{{variable}}'} placeholders · Fill values · Save · Run workflow to generate</div>
                    </>
                )}

            </Styled></ExpandedBlock> : null}
        </Main>
    </>);
}

const Main = styled.div`
    min-width: 150px;
    padding: 0;

    .doc-output-preview {
        display: flex;
        align-items: center;
        gap: 10px;
        padding: 10px 12px;
        background: #f0f9ff;
        border: 1px solid #bae6fd;
        border-radius: 10px;
        margin-bottom: 4px;
        box-shadow: 0 2px 6px rgba(14,165,233,0.08);

        .doc-icon { font-size: 22px; flex-shrink: 0; }

        .doc-output-meta {
            display: flex;
            flex-direction: column;
            gap: 2px;
            flex: 1;
            min-width: 0;
        }

        .doc-name {
            font-size: 11.5px;
            font-weight: 600;
            color: #0369a1;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
        }

        .doc-meta-detail {
            font-size: 10px;
            color: #7dd3fc;
        }

        .doc-download-btn {
            font-size: 11.5px;
            font-weight: 600;
            color: #0ea5e9;
            background: transparent;
            border: 1.5px solid #0ea5e9;
            border-radius: 7px;
            padding: 4px 12px;
            flex-shrink: 0;
            cursor: pointer;
            transition: all 0.15s ease;
            white-space: nowrap;
            outline: none;

            &:hover {
                background: #0ea5e9;
                color: #fff;
            }
        }
    }

    .doc-error {
        font-size: 11.5px;
        color: #dc2626;
        background: #fef2f2;
        border: 1px solid #fecaca;
        border-radius: 8px;
        padding: 6px 10px;
        margin-bottom: 4px;
    }
`;

const Styled = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;

    .section-divider {
        display: flex;
        align-items: center;
        gap: 8px;
        margin-bottom: 2px;

        &::before, &::after {
            content: '';
            flex: 1;
            height: 1px;
        }
        &::before { background: linear-gradient(90deg, #e5e7eb, transparent); }
        &::after  { background: linear-gradient(90deg, transparent, #e5e7eb); }
    }

    .section-title {
        font-size: 11px;
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 0.06em;
        background: linear-gradient(135deg, #0284c7, #0ea5e9);
        -webkit-background-clip: text;
        -webkit-text-fill-color: transparent;
        background-clip: text;
        white-space: nowrap;
    }

    .field-group {
        display: flex;
        flex-direction: column;
        gap: 5px;

        label {
            font-size: 11.5px;
            font-weight: 600;
            color: #6b7280;
            letter-spacing: 0.01em;
        }

        select, input[type="text"] {
            font-size: 13px;
            padding: 7px 10px;
            border: 1px solid #e5e7eb;
            border-radius: 9px;
            background: #fafafa;
            color: #111827;
            outline: none;
            width: 100%;
            transition: border-color 0.15s, box-shadow 0.15s;

            &:focus {
                border-color: #0ea5e9;
                box-shadow: 0 0 0 3px rgba(14,165,233,0.12);
                background: #fff;
            }
        }
    }

    .row-2 {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 10px;
    }

    .helper-text {
        font-size: 11px;
        color: #9ca3af;
        font-style: italic;
        padding-top: 2px;
    }

    .mode-toggle {
        display: flex;
        gap: 8px;
        margin-bottom: 4px;

        .mode-btn {
            flex: 1;
            padding: 8px 12px;
            font-size: 12px;
            font-weight: 600;
            border: 1.5px solid #e5e7eb;
            border-radius: 8px;
            background: #fafafa;
            color: #6b7280;
            cursor: pointer;
            transition: all 0.15s ease;

            &:hover {
                border-color: #0ea5e9;
                color: #0ea5e9;
            }

            &.active {
                background: linear-gradient(135deg, #0284c7, #0ea5e9);
                border-color: #0ea5e9;
                color: #fff;
            }
        }
    }

    .upload-area {
        input[type="file"] {
            display: none;
        }

        .upload-label {
            display: block;
            padding: 10px 14px;
            font-size: 12px;
            font-weight: 500;
            text-align: center;
            border: 2px dashed #d1d5db;
            border-radius: 8px;
            background: #f9fafb;
            color: #6b7280;
            cursor: pointer;
            transition: all 0.15s ease;

            &:hover {
                border-color: #0ea5e9;
                background: #f0f9ff;
                color: #0ea5e9;
            }
        }
    }

    .extract-error {
        font-size: 11px;
        color: #dc2626;
        background: #fef2f2;
        border: 1px solid #fecaca;
        border-radius: 6px;
        padding: 6px 10px;
        margin-top: 4px;
    }

    .template-info {
        font-size: 11px;
        color: #059669;
        background: #ecfdf5;
        border: 1px solid #a7f3d0;
        border-radius: 6px;
        padding: 6px 10px;
        margin-top: 4px;
    }

    .variables-section {
        display: flex;
        flex-direction: column;
        gap: 8px;

        > label {
            font-size: 11.5px;
            font-weight: 600;
            color: #6b7280;
        }

        .variables-list {
            display: flex;
            flex-direction: column;
            gap: 8px;
            max-height: 200px;
            overflow-y: auto;
            padding-right: 4px;

            &::-webkit-scrollbar {
                width: 6px;
            }

            &::-webkit-scrollbar-thumb {
                background: #d1d5db;
                border-radius: 3px;
            }
        }

        .variable-input {
            display: flex;
            flex-direction: column;
            gap: 5px;

            label {
                font-size: 12px;
                font-weight: 600;
                color: #374151;
                background: #f3f4f6;
                padding: 3px 10px;
                border-radius: 5px;
                font-family: monospace;
            }

            input {
                font-size: 13px;
                padding: 8px 12px;
                border: 1px solid #e5e7eb;
                border-radius: 7px;
                background: #fff;
                color: #111827;
                outline: none;
                transition: border-color 0.15s;

                &:focus {
                    border-color: #0ea5e9;
                    box-shadow: 0 0 0 2px rgba(14,165,233,0.1);
                }
            }
        }
    }

    .generate-btn {
        padding: 10px 16px;
        font-size: 12px;
        font-weight: 600;
        border: none;
        border-radius: 8px;
        background: linear-gradient(135deg, #059669, #10b981);
        color: #fff;
        cursor: pointer;
        transition: all 0.15s ease;

        &:hover {
            transform: translateY(-1px);
            box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
        }

        &:disabled {
            opacity: 0.5;
            cursor: not-allowed;
            transform: none;
            box-shadow: none;
        }
    }

    .upstream-hint {
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 8px 10px;
        background: #f0f9ff;
        border: 1px solid #bae6fd;
        border-radius: 8px;
        font-size: 11px;
        color: #0369a1;

        .upstream-hint-icon {
            font-size: 13px;
            flex-shrink: 0;
        }

        .upstream-hint-text {
            line-height: 1.4;
        }
    }
`;
